import { Component } from 'preact';
import { TEXT_BODY, TEXT_MUTED, INPUT_STYLE, GOLD } from '../../lib/medievalTheme';
import { Section, AdminButton } from '../AdminPanel';
import { api } from '../../utils/serverClient';
import { ACHIEVEMENTS } from '../../utils/arena/achievements';
import { pushAchievementToast } from '../../utils/arena/achievementToasts';
import { hasCap } from '../../utils/staffAccess';

// Settings tab is the catch-all for dev/test toggles plus the two
// access lists. Who can reach the RC channel, and who holds a staff
// role. The achievement dev controls live here because they're only
// useful while iterating on the claim ceremony.
const ROLE_BLURB = {
  admin: 'Everything.',
  moderator: 'Moderation: lookup, audit, ban, rename, match logs, chat. No economy.',
  qa: 'Fixture powers on the RC server: grants, cards, level, dev achievements. A row here on prod unlocks the RC channel and the QA title only.',
};

export default class AdminSettingsTab extends Component {
  state = {
    grantBusy: false,
    clearBusy: false,
    lastMessage: null,
    rcAllowlist: null,           // null until first fetch; array once loaded
    rcAllowlistLoading: false,
    rcAllowlistError: null,
    rcAllowlistInput: '',
    rcAllowlistBusyEmail: null,  // email currently being added/removed, for spinner state
    staffRoles: null,
    staffRolesLoading: false,
    staffRolesError: null,
    staffEmailInput: '',
    staffRoleInput: 'qa',
    staffBusyEmail: null,
    audit: null,
  };

  componentDidMount() {
    if (hasCap(this.props.profile, 'rc.allowlist')) this.fetchRcAllowlist();
    if (hasCap(this.props.profile, 'staff.manage')) {
      this.fetchStaffRoles();
      this.fetchAudit();
    }
  }

  fetchRcAllowlist = async () => {
    this.setState({ rcAllowlistLoading: true, rcAllowlistError: null });
    try {
      const res = await api.get('/admin/rc-allowlist');
      this.setState({
        rcAllowlist: Array.isArray(res?.entries) ? res.entries : [],
        rcAllowlistLoading: false,
      });
    } catch (err) {
      console.error('rc-allowlist fetch failed:', err);
      this.setState({
        rcAllowlistError: err?.message || 'Failed to load allowlist',
        rcAllowlistLoading: false,
      });
    }
  };

  handleAddRcEmail = async (e) => {
    e?.preventDefault?.();
    const email = this.state.rcAllowlistInput.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      this.setState({ rcAllowlistError: 'Enter a valid email' });
      return;
    }
    this.setState({ rcAllowlistBusyEmail: email, rcAllowlistError: null });
    try {
      await api.post('/admin/rc-allowlist', { email });
      this.setState({ rcAllowlistInput: '' });
      await this.fetchRcAllowlist();
    } catch (err) {
      console.error('rc-allowlist add failed:', err);
      this.setState({ rcAllowlistError: err?.message || 'Add failed' });
    } finally {
      this.setState({ rcAllowlistBusyEmail: null });
    }
  };

  handleRemoveRcEmail = async (email) => {
    this.setState({ rcAllowlistBusyEmail: email, rcAllowlistError: null });
    try {
      await api.delete(`/admin/rc-allowlist/${encodeURIComponent(email)}`);
      await this.fetchRcAllowlist();
    } catch (err) {
      console.error('rc-allowlist remove failed:', err);
      this.setState({ rcAllowlistError: err?.message || 'Remove failed' });
    } finally {
      this.setState({ rcAllowlistBusyEmail: null });
    }
  };

  fetchStaffRoles = async () => {
    this.setState({ staffRolesLoading: true, staffRolesError: null });
    try {
      const res = await api.get('/admin/staff-roles');
      this.setState({
        staffRoles: Array.isArray(res?.entries) ? res.entries : [],
        staffRolesLoading: false,
      });
    } catch (err) {
      console.error('staff-roles fetch failed:', err);
      this.setState({
        staffRolesError: err?.message || 'Failed to load staff roles',
        staffRolesLoading: false,
      });
    }
  };

  fetchAudit = async () => {
    try {
      const res = await api.get('/admin/staff-audit?limit=25');
      this.setState({ audit: Array.isArray(res?.entries) ? res.entries : [] });
    } catch (err) {
      console.error('staff-audit fetch failed:', err);
    }
  };

  handleAddStaffRole = async (e) => {
    e?.preventDefault?.();
    const email = this.state.staffEmailInput.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      this.setState({ staffRolesError: 'Enter a valid email' });
      return;
    }
    this.setState({ staffBusyEmail: email, staffRolesError: null });
    try {
      await api.post('/admin/staff-roles', { email, role: this.state.staffRoleInput });
      this.setState({ staffEmailInput: '' });
      this.props.addLog?.(`Granted ${this.state.staffRoleInput} to ${email}`);
      await this.fetchStaffRoles();
    } catch (err) {
      console.error('staff-role add failed:', err);
      this.setState({ staffRolesError: err?.message || 'Add failed' });
    } finally {
      this.setState({ staffBusyEmail: null });
    }
  };

  handleRemoveStaffRole = async (email, role) => {
    this.setState({ staffBusyEmail: `${email}:${role}`, staffRolesError: null });
    try {
      await api.delete(`/admin/staff-roles/${encodeURIComponent(email)}?role=${encodeURIComponent(role)}`);
      this.props.addLog?.(`Revoked ${role} from ${email}`);
      await this.fetchStaffRoles();
    } catch (err) {
      console.error('staff-role remove failed:', err);
      this.setState({ staffRolesError: err?.message || 'Remove failed' });
    } finally {
      this.setState({ staffBusyEmail: null });
    }
  };

  applyResult = (verb, result) => {
    const { onUpdateProfile, profile, addLog } = this.props;
    if (onUpdateProfile && profile) {
      onUpdateProfile({
        ...profile,
        achievements: result.achievements ?? profile.achievements ?? [],
        unclaimedAchievements: result.unclaimedAchievements ?? [],
        // clear endpoint also returns the new keystone total so the
        // header counter syncs without a refetch.
        keystones: result.keystones != null ? result.keystones : profile.keystones,
      });
    }
    const message = `${verb}: ${result.achievements?.length ?? 0} earned, ${result.unclaimedAchievements?.length ?? 0} unclaimed`;
    this.setState({ lastMessage: message });
    addLog?.(message);
  };

  handleGrantAll = async () => {
    this.setState({ grantBusy: true });
    try {
      const result = await api.post('/admin/dev-achievements/grant-all', {});
      this.applyResult('Granted all achievements', result);
    } catch (err) {
      console.error('grant-all failed:', err);
      this.setState({ lastMessage: `Grant failed: ${err?.message || err}` });
    } finally {
      this.setState({ grantBusy: false });
    }
  };

  // Client-side only. Fires the herald toast with a random
  // achievement, no server write. Clicking the banner opens the claim
  // ceremony only if that achievement happens to be unclaimed on this
  // account; otherwise the click just dismisses it.
  handleTestBanner = () => {
    const a = ACHIEVEMENTS[Math.floor(Math.random() * ACHIEVEMENTS.length)];
    pushAchievementToast(a.id);
    this.setState({ lastMessage: `Test banner: ${a.name}` });
  };

  handleClear = async () => {
    this.setState({ clearBusy: true });
    try {
      const result = await api.post('/admin/dev-achievements/clear', {});
      this.applyResult('Cleared all achievements', result);
    } catch (err) {
      console.error('clear failed:', err);
      this.setState({ lastMessage: `Clear failed: ${err?.message || err}` });
    } finally {
      this.setState({ clearBusy: false });
    }
  };

  render() {
    const { grantBusy, clearBusy, lastMessage } = this.state;
    const { profile } = this.props;
    return (
      <>
        {hasCap(profile, 'dev.achievements') && (
          <Section title="Dev. Achievements">
            <p className="text-xs mb-3" style={{ color: TEXT_MUTED, lineHeight: 1.5 }}>
              Toggles for testing the click-to-claim flow on your own account.
              Grant inserts every achievement as <strong>unclaimed</strong>, so
              the panel pulses and each row needs a click to pay out the keystone.
              Clear wipes every row and resets your keystone balance to zero.
            </p>
            <div className="flex items-center gap-2">
              <AdminButton onClick={this.handleGrantAll} disabled={grantBusy || clearBusy}>
                {grantBusy ? 'Granting…' : 'Grant all (unclaimed)'}
              </AdminButton>
              <AdminButton onClick={this.handleClear} disabled={grantBusy || clearBusy}>
                {clearBusy ? 'Clearing…' : 'Clear + reset keystones'}
              </AdminButton>
              <AdminButton onClick={this.handleTestBanner}>
                Test unlock banner
              </AdminButton>
            </div>
            {lastMessage ? (
              <p className="text-xs mt-3" style={{ color: TEXT_BODY }}>
                {lastMessage}
              </p>
            ) : null}
          </Section>
        )}
        {hasCap(profile, 'staff.manage') && (
          <Section title="Staff roles">
            <p className="text-xs mb-3" style={{ color: TEXT_MUTED, lineHeight: 1.5 }}>
              A role grants powers on <strong>this server only</strong>, and an
              account may hold several. Everyone on the RC server has QA powers
              there without a row. A <strong>qa</strong> row on prod unlocks the
              Release Candidates channel and the QA title, nothing more.
            </p>
            {this.renderStaffRoles()}
          </Section>
        )}
        {hasCap(profile, 'rc.allowlist') && (
          <Section title="Release Candidates allowlist">
            <p className="text-xs mb-3" style={{ color: TEXT_MUTED, lineHeight: 1.5 }}>
              Emails listed here can switch the desktop app to the Release
              Candidates channel from Settings → Release Channel. The toggle is
              greyed out for everyone else. Adds and removes take effect on the
              next eligibility check (next Settings open or next channel switch
              attempt). No client restart needed. Anyone with a staff role is
              eligible without being listed separately.
            </p>
            {this.renderRcAllowlist()}
          </Section>
        )}
        {hasCap(profile, 'staff.manage') && (
          <Section title="Recent staff actions">
            {this.renderAudit()}
          </Section>
        )}
      </>
    );
  }

  renderStaffRoles() {
    const { staffRoles, staffRolesLoading, staffRolesError, staffEmailInput, staffRoleInput, staffBusyEmail } = this.state;
    return (
      <div className="flex flex-col gap-3">
        <form onSubmit={this.handleAddStaffRole} className="flex items-center gap-2">
          <input
            type="email"
            value={staffEmailInput}
            onInput={(e) => this.setState({ staffEmailInput: e.target.value })}
            placeholder="email@example.com"
            className="flex-1 px-3 py-1.5 text-[0.6875cqw]"
            style={INPUT_STYLE}
          />
          <select
            value={staffRoleInput}
            onChange={(e) => this.setState({ staffRoleInput: e.target.value })}
            className="px-2 py-1.5 text-[0.6875cqw]"
            style={{ ...INPUT_STYLE, color: TEXT_BODY }}
          >
            <option value="qa">qa</option>
            <option value="moderator">moderator</option>
            <option value="admin">admin</option>
          </select>
          <AdminButton onClick={this.handleAddStaffRole} disabled={!staffEmailInput.trim() || staffBusyEmail != null}>
            Grant
          </AdminButton>
        </form>
        <p className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>{ROLE_BLURB[staffRoleInput]}</p>
        {staffRolesError ? (
          <p className="text-xs" style={{ color: '#d87a7a' }}>{staffRolesError}</p>
        ) : null}
        {staffRolesLoading && staffRoles == null ? (
          <p className="text-xs" style={{ color: TEXT_MUTED }}>Loading…</p>
        ) : null}
        {staffRoles && staffRoles.length === 0 ? (
          <p className="text-xs" style={{ color: TEXT_MUTED }}>
            No staff roles on this server. The ADMIN_EMAIL env var still applies and isn't listed here.
          </p>
        ) : null}
        {staffRoles && staffRoles.length > 0 ? (
          <ul
            className="flex flex-col rounded"
            style={{ background: 'rgba(0,0,0,0.25)', border: `1px solid ${GOLD} 0.18)` }}
          >
            {staffRoles.map((entry) => {
              const busy = staffBusyEmail === `${entry.email}:${entry.role}`;
              return (
                <li
                  key={`${entry.email}:${entry.role}`}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                  style={{ borderBottom: `1px solid rgba(166,160,155,0.08)` }}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-[0.75cqw] truncate" style={{ color: TEXT_BODY }}>
                      {entry.email} · {entry.role}
                    </span>
                    <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
                      added {new Date(entry.addedAt).toLocaleDateString()}
                      {entry.addedBy ? ` · by ${entry.addedBy}` : ''}
                    </span>
                  </div>
                  <AdminButton onClick={() => this.handleRemoveStaffRole(entry.email, entry.role)} disabled={busy}>
                    {busy ? 'Revoking…' : 'Revoke'}
                  </AdminButton>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    );
  }

  renderAudit() {
    const { audit } = this.state;
    if (audit == null) return <p className="text-xs" style={{ color: TEXT_MUTED }}>Loading…</p>;
    if (audit.length === 0) return <p className="text-xs" style={{ color: TEXT_MUTED }}>Nothing logged yet.</p>;
    return (
      <div className="flex flex-col gap-0.5 max-h-40 overflow-y-auto">
        {audit.map((entry, i) => (
          <div key={i} className="text-[0.625cqw]" style={{ color: TEXT_BODY }}>
            <span style={{ color: TEXT_MUTED }}>{new Date(entry.at).toLocaleString()}</span>
            {' '}{entry.actor} ({entry.role}) → {entry.action}
            {entry.target ? ` · ${entry.target}` : ''}
          </div>
        ))}
      </div>
    );
  }

  renderRcAllowlist() {
    const { rcAllowlist, rcAllowlistLoading, rcAllowlistError, rcAllowlistInput, rcAllowlistBusyEmail } = this.state;
    return (
      <div className="flex flex-col gap-3">
        <form onSubmit={this.handleAddRcEmail} className="flex items-center gap-2">
          <input
            type="email"
            value={rcAllowlistInput}
            onInput={(e) => this.setState({ rcAllowlistInput: e.target.value })}
            placeholder="email@example.com"
            className="flex-1 px-3 py-1.5 text-[0.6875cqw]"
            style={INPUT_STYLE}
          />
          <AdminButton onClick={this.handleAddRcEmail} disabled={!rcAllowlistInput.trim() || rcAllowlistBusyEmail != null}>
            Add
          </AdminButton>
        </form>
        {rcAllowlistError ? (
          <p className="text-xs" style={{ color: '#d87a7a' }}>{rcAllowlistError}</p>
        ) : null}
        {rcAllowlistLoading && rcAllowlist == null ? (
          <p className="text-xs" style={{ color: TEXT_MUTED }}>Loading…</p>
        ) : null}
        {rcAllowlist && rcAllowlist.length === 0 ? (
          <p className="text-xs" style={{ color: TEXT_MUTED }}>
            No allowlisted accounts yet. Add yourself first so the toggle shows up in your Settings.
          </p>
        ) : null}
        {rcAllowlist && rcAllowlist.length > 0 ? (
          <ul
            className="flex flex-col rounded"
            style={{ background: 'rgba(0,0,0,0.25)', border: `1px solid ${GOLD} 0.18)` }}
          >
            {rcAllowlist.map((entry) => {
              const busy = rcAllowlistBusyEmail === entry.email;
              return (
                <li
                  key={entry.email}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                  style={{ borderBottom: `1px solid rgba(166,160,155,0.08)` }}
                >
                  <div className="flex flex-col min-w-0">
                    <span className="text-[0.75cqw] truncate" style={{ color: TEXT_BODY }}>{entry.email}</span>
                    <span className="text-[0.5625cqw]" style={{ color: TEXT_MUTED }}>
                      added {new Date(entry.addedAt).toLocaleDateString()}
                      {entry.addedBy ? ` · by ${entry.addedBy}` : ''}
                    </span>
                  </div>
                  <AdminButton onClick={() => this.handleRemoveRcEmail(entry.email)} disabled={busy}>
                    {busy ? 'Removing…' : 'Remove'}
                  </AdminButton>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    );
  }
}
